import { useT } from '@/components/visitone/visitone-provider'
import { PageHeader } from '@/components/visitone/page-header'
import { SetupAreaCard } from '@/components/visitone/setup-area-card'
import {
  SETUP_GUEST_DATA_ACCESS_PATH,
  SETUP_LANDLORDS_PATH,
  SETUP_ORDER_TYPES_PATH,
  SETUP_PEOPLE_PATH,
} from '@/lib/routing'

/*
 * Setup index (VIS-328): the Setup areas the user can open. Ordering follows
 * the setup destination; People & access also needs `users.view` (VIS-332).
 * Landlords follows `landlords.manage`, which is a principal action and does
 * not require that destination (VIS-329). Guest data access follows the
 * principal action `guest_access.produce`, held only by Landlords (VIS-330).
 */
export function SetupIndexSurface({
  canManageLandlords,
  canOpenSetup,
  canViewPeople = false,
  canProduceGuestAccess = false,
  tenantName = '',
}: {
  canManageLandlords: boolean
  canProduceGuestAccess?: boolean
  canOpenSetup: boolean
  canViewPeople?: boolean
  tenantName?: string
}) {
  const t = useT()

  const areas = [
    ...(canOpenSetup
      ? [
          {
            id: 'ordering',
            to: SETUP_ORDER_TYPES_PATH,
            name: t('setup.areas.ordering.name'),
            description: t('setup.areas.ordering.description'),
            topics: t('setup.areas.ordering.topics'),
          },
        ]
      : []),
    ...(canViewPeople
      ? [
          {
            id: 'people',
            to: SETUP_PEOPLE_PATH,
            name: t('setup.areas.people.name'),
            description: t('setup.areas.people.description', { tenant: tenantName }),
            topics: t('setup.areas.people.topics'),
          },
        ]
      : []),
    ...(canManageLandlords
      ? [
          {
            id: 'landlords',
            to: SETUP_LANDLORDS_PATH,
            name: t('setup.areas.landlords.name'),
            description: t('setup.areas.landlords.description'),
            audience: t('setup.landlordsOnly'),
          },
        ]
      : []),
    ...(canProduceGuestAccess
      ? [
          {
            id: 'guest-access',
            to: SETUP_GUEST_DATA_ACCESS_PATH,
            name: t('setup.areas.guestAccess.name'),
            description: t('setup.areas.guestAccess.description'),
            audience: t('setup.landlordsOnly'),
          },
        ]
      : []),
  ]

  return (
    <div className="flex max-w-5xl flex-col gap-4">
      <PageHeader title={t('setup.title')} summary={t('setup.summary')} />
      <ul className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-label={t('setup.areasLabel')}>
        {areas.map(({ id, ...area }) => (
          <li key={id}>
            <SetupAreaCard
              {...area}
              accessibleName={t('setup.cardName', { name: area.name, description: area.description })}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}
